/**
 * Decision and intent factories shared by the viewport search core and policies.
 *
 * A decision describes the one search change for a settled map position. An intent
 * describes a camera or gesture effect a policy asks the core to perform in
 * response to a lifecycle event. Both are frozen plain objects whose `type` is a
 * ViewportSearchDecisionType or ViewportSearchIntentType.
 *
 * @module searchResultsMap/services/decisions
 */

import ViewportSearchDecisionType from '../enums/viewportSearchDecisionType';
import ViewportSearchIntentType from '../enums/viewportSearchIntentType';

export function none() {
  return Object.freeze({ type: ViewportSearchDecisionType.None });
}

export function enterFocused(bounds) {
  return Object.freeze({ type: ViewportSearchDecisionType.EnterFocused, bounds });
}

export function updateFocused(bounds) {
  return Object.freeze({ type: ViewportSearchDecisionType.UpdateFocused, bounds });
}

export function exitFocused() {
  return Object.freeze({ type: ViewportSearchDecisionType.ExitFocused });
}

export function searchRectangle(bounds) {
  return Object.freeze({ type: ViewportSearchDecisionType.SearchRectangle, bounds });
}

/**
 * @param {{ south: number, north: number, west: number, east: number }} bounds
 *   Search bounds already clamped to the state.
 */
export function refreshState(bounds) {
  return Object.freeze({ type: ViewportSearchDecisionType.RefreshState, bounds });
}

export function recenter(latitude, longitude) {
  return Object.freeze({ type: ViewportSearchIntentType.Recenter, latitude, longitude });
}

export function setZoom(zoom) {
  return Object.freeze({ type: ViewportSearchIntentType.SetZoom, zoom });
}

export function fitBounds(bounds) {
  return Object.freeze({ type: ViewportSearchIntentType.FitBounds, bounds });
}

export function grantGesture() {
  return Object.freeze({ type: ViewportSearchIntentType.GrantGesture });
}

export function deferUntilIdle(intents) {
  return Object.freeze({ type: ViewportSearchIntentType.DeferUntilIdle, intents: Object.freeze([...intents]) });
}

export function dispatchDecision(decision) {
  return Object.freeze({ type: ViewportSearchIntentType.DispatchDecision, decision });
}
